/*
 * Copyright (c) 2023. All rights are reserved to Newport Collection
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Button, Grid, List, Typography } from '@material-ui/core';
import { useTranslation } from 'tools/i18n';
import { useRouter } from 'next/router';
import { TrackingContext } from 'components/providers/tracking';
import SearchIcon from 'public/images/icons/search-thin.svg';
import CloseIcon from "public/images/icons/close.svg";
import elevateSearch from '../methods/elevateSearch';
import { getFallbackSuggestions, getLiveSuggestions } from '../methods/getSuggestions';
import { SearchResultBtn, SearchResultProduct, SearchSuggestion } from '../parts';
import coreStyle from '../style/coreStyle';
import useSessionStore from '@services/stores/SessionStore';
import useApplicationStore from '@services/stores/ApplicationStore';
import SearchResultContentItem from '@components/header/search/parts/SearchResultContentItem';

const useStyles = makeStyles(() => coreStyle);
let inputTimeout;
let focusTimeout;

export default function SearchCore() {
	const router = useRouter();
	const searchInputRef = useRef();
	const classes = useStyles();
	const { t } = useTranslation();
	const { actions } = useContext(TrackingContext);
	const { config, salesChannel } = useSessionStore((state) => state.session);

    const { toggleDrawer } = useApplicationStore((state) => state.methods);
    const focus = useApplicationStore((state) => state.modals.searchOpen);

    const [state, setState] = useState({
		dirty: false,
		products: [],
		content: [],
		suggestions: [],
		hitCount: 0,
	});
	const { dirty, products, content, suggestions, hitCount } = state;
	const [query, setQuery] = useState('');
	const [suggestedItems, setSuggestedItems] = useState([]);

	const glued = !router.asPath?.includes('sok') ? 'glued' : '';
    const isMobile = useApplicationStore((state) => state.isMobile);


	useEffect(async () => {
		if (suggestedItems.length === 0) {
			await getLiveSuggestions(salesChannel).then(res => {
				setSuggestedItems(res);
			});
		}
	}, [salesChannel]);


	//useEffect(async () => { onFocus(); }, [focus]);

	useEffect(async () => {
		if (focus && !dirty && suggestedItems.length === 0) {
			await getLiveSuggestions(salesChannel).then(res => {
				setSuggestedItems(res);
			});
		}
	}, [focus, dirty]);

    useEffect(() => { onFocus() }, [focus]);

	useEffect(() => {
		if (query.length > 2) {
			setState(state => {
				return { ...state, dirty: true };
			});
		} else {
			setState(state => {
				return { ...state, products: [], content: [], suggestions: [], dirty: false };
			});
		}
	}, [query]);



	function onChange(e) {
		const query = e.target.value;
		setQuery(query);

		clearTimeout(inputTimeout);
		if (query.length > 2) {
			inputTimeout = setTimeout(async () => {
				await elevateSearch(query, config, isMobile, state, setState);
			}, 200);
		}
	}

	function onFocus() {
		if (focus) {
			actions.click({
				type: 'open_search',
				text: t('search'),
				identifier: router.asPath
			});
			clearTimeout(focusTimeout);
			focusTimeout = setTimeout(() => {
				searchInputRef.current.focus();
			}, 150);
		} else {
			searchInputRef.current.blur();
		}
	}

	function onKeyDown(e) {
		if (e.key === 'Enter') {
			goToSearchResults();
		}
	}

	function goToSearchResults() {
		if (query.length > 2 && focus) {
            router.push({
                pathname: t('search-url'),
                query: { q: query }
            }).then();
		} else {
			toggleDrawer('searchOpen')
		}
	}

	function clearSearch() {
		searchInputRef.current.focus();
        toggleDrawer('searchOpen')
		setQuery('');
	}

	function Controls() {
		return (
			<Button
				onClick={ !focus ? goToSearchResults : clearSearch}
				className={[classes.icon__button, glued, classes.icon__button_control, focus ? 'focus' : ''].join(' ')}
			>
                {!focus ?
                    <SearchIcon className={[classes.control__icon, focus ? 'focus' : ''].join(' ')} />
                    :
                    <CloseIcon className={[classes.control__icon, focus ? 'focus' : ''].join(' ')} />
                }
			</Button>
		)
	}

	const wrapperClasses = [
		classes.search__wrapper,
		focus ? 'focus' : '',
		dirty ? 'dirty' : '',
	].join(' ');

	const autocompleteClasses = [
		classes.autocomplete,
		glued,
		focus ? 'focus' : '',
	].join(' ');

	return (
		<>
			<div className={wrapperClasses}>

                <div className={[classes.search__inner].join(' ')}>
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t('search')}
                        name="search"
                        value={query}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        autoComplete="one-time-code"
                        autoCorrect="off"
                        className={[classes.search__input, focus].join(' ')}
                        onFocus={() => {
                            toggleDrawer('searchOpen', true)
                        }}
                    />
                    <Controls />
                </div>

				<div className={autocompleteClasses}>
					<Grid container>
						<Grid xs={12} className={classes.suggestions__wrapper} item>
							{suggestions.length > 0 ? (
								<List disablePadding>
									{suggestions.map((suggestion, index) => (
										<SearchSuggestion props={{ suggestion, index, query, classes, t, focus }} />
									))}
								</List>
							) : (
								<>
									<Typography className={classes.relatedSearches} variant="body1">{t('search-popular-searches')}</Typography>
									<List disablePadding>
										{getFallbackSuggestions(salesChannel).map((suggestion, index) => (
											<SearchSuggestion key={index} props={{ suggestion, index, query, classes, t, focus }} />
										))}
									</List>
								</>
							)}
						</Grid>
                        <Grid className={classes.resultsContainer} container>
                            <Grid xs={12} item>

							    {products.length > 0 ? (
                                    <Grid className={classes.products__wrapper} spacing={1} container>
                                        {products.map((product, index) => (
                                            <SearchResultProduct key={product.id} props={{ product, index, classes, config, focus, isMobile }} />
                                        ))}
                                    </Grid>
                                ) : query.length > 2 && content.length === 0 ? (
                                    <Typography className={classes.noResultsText} variant="body1">
                                        {t('search-result-no-hit')}
                                    </Typography>
                                ) : suggestedItems.length > 0 && content.length === 0? (
                                    <Grid className={classes.products__wrapper} spacing={1} container>
                                        {suggestedItems.map((product, index) => (
                                            <SearchResultProduct key={product.id} props={{ product, index, classes, config, focus }} />
                                        ))}
                                    </Grid>
                                ) : (
                                    ''
                                )}

                                {content.length > 0 && (
                                    <Grid className={classes.products__wrapper} spacing={1} container>
                                        { content.map(item => (
                                            <SearchResultContentItem key={item.key} props={{classes, item, source: 'dropdown'}} />
                                        ))}
                                    </Grid>
                                )}
						    </Grid>
						</Grid>

                        {hitCount > 6 && query &&
                            <Grid xs={12} className={classes.showMoreItem} item>
                                <SearchResultBtn query={query} classes={classes} />
                            </Grid>
                        }
					</Grid>
				</div>
			</div>
		</>
	);
}
